import { useId, useState } from 'react';
import { useT } from '../../i18n';
import { safeId } from '../ui/labelledControl';
import {
  type OptionBounds,
  type RejectionReason,
  addCustomOption,
  isCustomOption,
  mergedOptions,
} from './customOptions';

// ─────────────────────────────────────────────────────────────────────
// ONE CHIP ROW, PLUS A FIELD FOR THE ADMIN'S OWN NUMBER.
//
// The booking settings have three of these — smallest gap, allowed durations,
// how far ahead bookings open — and they were three near-identical blocks of
// JSX with a hard-coded array in the middle. Adding "and a custom value" to
// each separately is how the three quietly stop behaving the same: one sorts,
// one does not; one refuses duplicates, one adds a second chip.
//
// So the row is one component and the differences are props. The only real
// difference is `mode`: the gap and the window pick ONE value, durations pick
// a SET. Everything else — merging, sorting, which chips may be deleted, what
// a rejected value says — is shared by construction.
// ─────────────────────────────────────────────────────────────────────

export interface OptionPickerProps {
  /** The product's own chips. Never deletable. */
  builtIn: readonly number[];
  /** The club's own chips, as stored in the booking config. */
  custom: number[];
  /** 'single' — one value (gap, window). 'multi' — a set (durations). */
  mode: 'single' | 'multi';
  /** The chosen value ('single') or the chosen set ('multi'). */
  selected: number | number[];
  /** A chip was clicked: set it, or toggle it into/out of the set. */
  onSelect: (value: number) => void;
  /** Accepted custom value; the parent stores the new list. */
  onAddCustom: (custom: number[]) => void;
  /** The × on a custom chip. The parent also fixes up the selection. */
  onRemoveCustom: (value: number) => void;
  /** How a value reads on its chip — «90 min», «14 dager». */
  labelFor: (value: number) => string;
  bounds: OptionBounds;
  /** Which unit the field is asking for, for the placeholder and the hint. */
  unit: 'minutes' | 'days';
  /**
   * Id of the visible heading over the picker — «Minste mellomrom» and the
   * like. It names the chip row as a group, so a screen reader reaching «30
   * min» knows what thirty minutes OF (#900).
   */
  labelledBy?: string;
}

export function OptionPicker({
  builtIn,
  custom,
  mode,
  selected,
  onSelect,
  onAddCustom,
  onRemoveCustom,
  labelFor,
  bounds,
  unit,
  labelledBy,
}: OptionPickerProps) {
  const t = useT();
  const errorId = safeId('option-picker-error', useId());
  const [raw, setRaw] = useState('');
  const [error, setError] = useState<RejectionReason | null>(null);

  const isSelected = (v: number) =>
    mode === 'single' ? selected === v : Array.isArray(selected) && selected.includes(v);

  const submit = () => {
    const outcome = addCustomOption(builtIn, custom, raw, bounds);
    if (outcome.status === 'added') {
      onAddCustom(outcome.custom);
      // Cleared only on success, so a rejected value stays on screen to be
      // corrected rather than making the admin retype it.
      setRaw('');
      setError(null);
      return;
    }
    setError(outcome.reason);
  };

  const errorText =
    error === 'invalid'
      ? t('booking.config.custom.errInvalid')
      : error === 'range'
        ? t('booking.config.custom.errRange', {
            min: String(bounds.min),
            max: String(bounds.max),
          })
        : error === 'duplicate'
          ? t('booking.config.custom.errDuplicate')
          : null;

  return (
    <>
      <div className="flex gap-1.5 flex-wrap" role="group" aria-labelledby={labelledBy}>
        {mergedOptions(builtIn, custom, selected).map((v) => {
          const removable = isCustomOption(builtIn, custom, v);
          const on = isSelected(v);
          return (
            <span
              key={v}
              className={`inline-flex items-center rounded-full transition-colors ${
                on
                  ? 'bg-emerald-700 text-white shadow'
                  : 'bg-bg-soft/40 text-ink-2 hover:bg-bg-soft/70 border border-line'
              }`}
            >
              <button
                onClick={() => onSelect(v)}
                aria-pressed={on}
                className={`text-xs font-semibold py-1.5 ${removable ? 'pl-3 pr-1.5' : 'px-3'}`}
              >
                {labelFor(v)}
              </button>
              {/* A SEPARATE button, not one nested in the other: nesting is
                  invalid HTML and the click would land on whichever the
                  browser decided. Only the club's own chips carry it. */}
              {removable && (
                <button
                  onClick={() => onRemoveCustom(v)}
                  aria-label={t('booking.config.custom.remove', { v: labelFor(v) })}
                  className={`text-xs leading-none pr-2.5 pl-0.5 py-1.5 opacity-70 hover:opacity-100 ${
                    on ? 'text-white' : 'text-ink-3'
                  }`}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <input
          type="number"
          inputMode="numeric"
          value={raw}
          min={bounds.min}
          max={bounds.max}
          onChange={(e) => {
            setRaw(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // The picker sits inside a settings dialog, not a form, but
              // Enter is what anyone typing a number will press.
              e.preventDefault();
              submit();
            }
          }}
          aria-label={
            unit === 'days'
              ? t('booking.config.custom.addDays')
              : t('booking.config.custom.addMinutes')
          }
          aria-invalid={errorText ? true : undefined}
          aria-describedby={errorText ? errorId : undefined}
          placeholder={
            unit === 'days'
              ? t('booking.config.custom.placeholderDays')
              : t('booking.config.custom.placeholderMinutes')
          }
          className="w-20 text-xs px-2 py-1.5 rounded-lg border border-line bg-bg-soft/30 text-ink-1"
        />
        <button
          onClick={submit}
          disabled={raw.trim() === ''}
          className="text-xs font-semibold px-3 py-1.5 rounded-full border border-line text-ink-2 hover:bg-bg-soft/70 disabled:opacity-40"
        >
          {t('booking.config.custom.add')}
        </button>
        {/* `role="alert"`: the message appears after Enter, away from where
            focus is, and would otherwise arrive in silence. */}
        {errorText && (
          <span id={errorId} role="alert" className="text-[11px] text-rose font-semibold">
            {errorText}
          </span>
        )}
      </div>
    </>
  );
}
